import { MultiSelectFilter } from "@/components/MultiSelectFilter";
import { useCatalogContext } from "@/hooks/useCatalog";
import { useProductLeadCounts } from "@/hooks/useProductLeadCounts";
import type { ProductCountFilters } from "@/hooks/useProductLeadCounts";
import { useCrmStore } from "@/stores/crm.store";

// Sentinel store value for the "no product assigned" bucket (productId IS NULL).
const NO_PRODUCT = "none";

/**
 * Product/program filter, backed by the shared crm.store `productIds`. Several
 * may be picked at once (OR), "Sin producto" included. Each option shows a
 * contextual lead count so the user isn't guessing which products have leads.
 * Renders nothing when the active catalog has no products.
 *
 * The counts always respect the other active filters. There used to be a `scope`
 * prop so the Pipeline could ask for unfiltered counts; with the board and the
 * table behind one filter bar there is only one scope.
 */
export function ProductFilterSelect(): React.JSX.Element | null {
  const { data: catalog } = useCatalogContext();
  const productIds = useCrmStore((s) => s.productIds);
  const toggleProduct = useCrmStore((s) => s.toggleProduct);
  const clearProducts = useCrmStore((s) => s.clearProducts);

  // The other active filters — never productIds itself, which is the dimension
  // being counted.
  const stages = useCrmStore((s) => s.stages);
  const channel = useCrmStore((s) => s.channel);
  const scoreMin = useCrmStore((s) => s.scoreMin);
  const scoreMax = useCrmStore((s) => s.scoreMax);
  const cycleIds = useCrmStore((s) => s.cycleIds);
  const search = useCrmStore((s) => s.search);
  const from = useCrmStore((s) => s.from);
  const to = useCrmStore((s) => s.to);
  const labelIds = useCrmStore((s) => s.labelIds);
  const assigneeIds = useCrmStore((s) => s.assigneeIds);

  const countFilters: ProductCountFilters = {
    stages: stages.join(","),
    channel,
    scoreMin,
    scoreMax,
    cycleIds: cycleIds.join(","),
    search,
    from,
    to,
    labelIds: labelIds.join(","),
    assigneeIds: assigneeIds.join(","),
  };
  const { data: countsData } = useProductLeadCounts(countFilters);

  const products = catalog?.products ?? [];
  if (products.length === 0) return null;

  const activeIds = new Set(products.map((p) => p.id));
  const countByProduct = new Map<string, number>();
  let noProductCount = 0;
  // Products that hold leads but are no longer in the active catalog. Archiving
  // (or, before the id-based diff, renaming) a program used to make its leads
  // unreachable from every filter — they simply had no option to select.
  const archived: Array<{ id: string; name: string; count: number }> = [];

  for (const c of countsData?.counts ?? []) {
    if (c.productId === null) {
      noProductCount = c.count;
      continue;
    }
    countByProduct.set(c.productId, c.count);
    if (!activeIds.has(c.productId) && c.count > 0) {
      archived.push({ id: c.productId, name: c.name ?? "Programa eliminado", count: c.count });
    }
  }
  archived.sort((a, b) => b.count - a.count);

  return (
    <MultiSelectFilter
      options={[
        ...products.map((p) => ({
          id: p.id,
          name: p.name,
          count: countByProduct.get(p.id) ?? 0,
        })),
        ...archived.map((p) => ({ id: p.id, name: `${p.name} (archivado)`, count: p.count })),
        { id: NO_PRODUCT, name: "Sin producto", count: noProductCount },
      ]}
      selected={productIds}
      onToggle={toggleProduct}
      onClear={clearProducts}
      placeholder="Todos los productos"
      pluralNoun="productos"
      singularNoun="producto"
      clearLabel="Todos los productos"
      ariaLabel="Producto"
      contentClassName="w-auto min-w-56 max-w-[min(28rem,calc(100vw-2rem))]"
    />
  );
}
